import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import ExperienceInfo from "../../components/UserComponents/ExperienceInfo";
import VendorInfo from "../../components/UserComponents/VendorInfo";
import { formatDate, getCleanedUrl } from "../../utils/HelperMethod";

import {
  FiDownload,
  FiMessageSquare,
  FiCheck,
  FiClock,
  FiUser,
  FiCalendar,
} from "react-icons/fi";

import { useAdminBookingById } from "../../custom-hooks/queries";
import BookingStatusTimeline from "../../components/common/BookingStatusTimeline";
import StatusBadge from "../../components/vendors/StatusBadge";

/* ================= SKELETON ================= */
const BookingDetailSkeleton = () => (
  <div className="w-full max-w-[1380px] mx-auto px-6 py-10 animate-pulse">
    <div className="h-8 w-64 bg-gray-200 rounded mb-6"></div>
    <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-10">
      <div className="w-full h-[320px] bg-gray-200 rounded-xl"></div>
      <div className="border rounded-2xl p-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded mb-3"></div>
        ))}
      </div>
    </div>
  </div>
);

export default function AdminBookingDetailPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useAdminBookingById(bookingId);

  const booking = data?.data || {};
  const experience = booking?.experience || {};
  const vendor = booking?.vendor || {};
  const timeline = booking?.bookingStatusTimeline || [];

  const vendorLogo = getCleanedUrl(vendor.logoUrl);

  /* 🔹 SAFE STATUS CHECKER */
  const hasStatus = (label) =>
    Array.isArray(timeline) && timeline.some((t) => t.status === label);

  /* 🔹 FIXED FLAGS (ERROR FIX) */
  const isBookingConfirmed = hasStatus("Booking Confirmed");
  const isUpcoming = hasStatus("Upcoming");
  const isAwaitingConfirmation = hasStatus("Awaiting User Confirmation");
  const isCompleted = hasStatus("Completed");

  if (isLoading) return <BookingDetailSkeleton />;

  if (isError || !booking.bookingId) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-semibold">Booking not found</h2>
        <button
          onClick={() => navigate("/admin/booking-management")}
          className="mt-4 px-6 py-2 bg-[#00C3CC] text-white rounded-lg"
        >
          Back to Bookings
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1380px] mx-auto px-6 py-1">
      <h1 className="text-[28px] font-semibold text-gray-900 mb-6">
        {experience.title}
      </h1>

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-10">
        {/* ================= LEFT ================= */}
        <div>
          {experience.images?.length > 0 && (
            <img
              src={experience.images[0]}
              className="w-full h-[320px] rounded-xl object-stretch object-center"
              alt=""
            />
          )}

          <div className="mt-10">
            <ExperienceInfo
              title={experience.title}
              rating={experience.rating}
              reviewsCount={experience.totalReviews}
              location={experience.location}
              price={experience.price}
              duration={experience.duration}
              category={experience.category}
              description={experience.description}
              priceType={experience.priceType}
            />
          </div>

          <div className="mt-10">
            <VendorInfo
              name={vendor.businessName}
              description={vendor.description}
              rating={vendor.rating}
              logoUrl={vendorLogo}
              totalReviews={vendor.totalReviews}
            />
          </div>
        </div>

        {/* ================= RIGHT ================= */}
        <div className="border border-gray-200 rounded-2xl p-6 shadow-sm h-fit">
          <h2 className="text-lg font-semibold mb-5">Booking Summary</h2>

          <SummaryRow label="Booking Code" value={booking.bookingCode} />
          <SummaryRow
            label="Booking Date"
            value={formatDate(booking.bookingDate)}
          />
          <SummaryRow label="Time Slot" value={booking.timeSlot} />
          <SummaryRow label="People" value={booking.people} />
          <SummaryRow label="Payment Method" value={booking.paymentMethod} />

          <div className="flex justify-between mt-4">
            <span className="font-medium">Payment Status</span>
            <StatusBadge status={booking.paymentStatus} />
          </div>

          <hr className="my-6" />

          <BookingStatusTimeline
            title={"Booking Status"}
            timeline={booking?.bookingStatusTimeline || []}
          />

          <hr className="my-6" />

          {booking?.notes && (
            <>
              <h2 className="text-lg font-semibold mb-4">Notes</h2>
              <p className="text-sm text-gray-700">• {booking.notes}</p>
              <hr className="my-6" />
            </>
          )}

          {/* <div className="flex flex-col gap-3">
            <ActionButton icon={<FiDownload />} label="Download Receipt" />
            <ActionButton
              icon={<FiMessageSquare />}
              label="Contact Vendor"
              onClick={() => navigate("/admin/support-ticket")}
            />
          </div> */}
        </div>
      </div>
    </div>
  );
}

/* ================= HELPERS ================= */

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between text-sm py-1">
    <span className="text-gray-600">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

/* 🔹 FIXED: StatusConnector */
const StatusConnector = ({ active }) => (
  <div className={`w-[2px] h-6 ${active ? "bg-[#00C3CC]" : "bg-gray-300"}`} />
);

/* 🔹 FIXED: StatusStep */
const StatusStep = ({ label, active, icon, showConnector = true }) => (
  <div className="flex items-start gap-4">
    <div className="flex flex-col items-center">
      <div
        className={`h-10 w-10 rounded-full flex items-center justify-center ${
          active ? "bg-[#00C3CC]" : "bg-gray-300"
        }`}
      >
        {React.cloneElement(icon, { className: "text-white", size: 20 })}
      </div>
      {showConnector && <StatusConnector active={active} />}
    </div>
    <p
      className={`font-medium text-[15px] mt-1 ${
        active ? "text-[#00C3CC]" : "text-gray-400"
      }`}
    >
      {label}
    </p>
  </div>
);

const ActionButton = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="border rounded-xl py-3 px-4 flex items-center gap-3 hover:bg-gray-100"
  >
    {icon}
    <span className="text-sm font-medium">{label}</span>
  </button>
);
